/**
 * 콜랩 상세 DTO → 화면 모델(ACL). 2.0 · 3.0 모두.
 * @description 서버 응답 모양이 바뀌면 여기만 고친다. 카드는 `collabV3CardMapper` 가 옮긴다.
 *
 *   참여자 줄 숫자와 주최자 본인 확인 규칙은 앱과 같다(`participantsRowCount` ·
 *   `isOrganizerVerified`).
 */
import {
  toCollabV3Checkin,
  toCollabV3Fee,
  toCollabV3Funding,
  toCollabV3Works,
} from "./collabV3CardMapper";
import { toCollabV3Person } from "./collabV3PersonMapper";
import { toCollabV3SelectedPreviews } from "./collabV3WorkMapper";
import { timeZoneOfCountry, toCollabV3Place } from "../utils/collabV3Place";
import type { CollabV3DetailDto } from "../dtos";
import type { CollabV3Detail } from "../entities";

/** 작품 카드면 올린 사람, 참가비면 예약한 사람, 그 밖에는 참여자(주최자 포함) */
function participantsRowCount({ cards, participantCount }: CollabV3DetailDto) {
  if (cards.works) return cards.works.uploaderCount;
  if (cards.fee) return cards.fee.reservedCount;
  return participantCount;
}

/** 유료 카드(모금 · 참가비)는 정산 정보를 등록해야 공개된다 — 운영 제공은 그 단계를 건너뛴다 */
const isHostVerified = (dto: CollabV3DetailDto): boolean =>
  (dto.cards.funding !== null || dto.cards.fee !== null) &&
  !dto.isOperatorProvided &&
  dto.statusBadge !== "DRAFT";

export function toCollabV3Detail(
  dto: CollabV3DetailDto,
  now: Date = new Date(),
): CollabV3Detail {
  const coverUrl = dto.cover.imageUrl?.trim() || null;
  const isCancelled = dto.status === "CANCELLED";
  const { cards, viewer } = dto;
  return {
    id: dto.collabId,
    version: dto.version,
    status: dto.status,
    badge: dto.statusBadge,
    isLinkOnly: dto.listingVisibility === "LINK_ONLY",
    isOperatorProvided: dto.isOperatorProvided,
    title: dto.title,
    description: dto.description.trim(),
    // 서버는 대표 그림 목록을 그대로 준다 — 커버와 같은 그림을 두 번 보이지 않는다
    descriptionImages: dto.descriptionImageUrls.filter(
      (url) => Boolean(url) && url !== coverUrl,
    ),
    coverUrl,
    artist: dto.artist
      ? { id: dto.artist.artistId, name: dto.artist.artistName }
      : null,
    organizer: {
      ...toCollabV3Person(dto.organizer),
      isSquare: dto.organizer.avatarShape === "SQUARE",
    },
    schedule: {
      startAt: dto.startAt,
      endAt: dto.endAt,
      isLegacy: dto.version === "V2",
      placeTimeZone: timeZoneOfCountry(dto.location?.country?.countryCode),
    },
    place: toCollabV3Place(dto.location),
    countryName: dto.location?.country?.countryName ?? null,
    cancelReason: isCancelled ? dto.cancelReason?.trim() || null : null,
    participantsRowCount: participantsRowCount(dto),
    participants: dto.participantsPreview.map(({ user }) =>
      toCollabV3Person(user),
    ),
    fee: cards.fee ? toCollabV3Fee(cards.fee, isCancelled, now) : null,
    funding: cards.funding ? toCollabV3Funding(cards.funding) : null,
    works: cards.works ? toCollabV3Works(cards.works, now) : null,
    selectedWorks: toCollabV3SelectedPreviews(dto.selectedWorks, dto.collabId),
    checkin: dto.checkin ? toCollabV3Checkin(dto.checkin) : null,
    isHostVerified: isHostVerified(dto),
    isGuest: !viewer.isOrganizer && !viewer.isParticipant,
    createdAt: dto.createdAt,
    shareImage: dto.shareImage ?? null,
  };
}
